import { IDropdownConfig, DeviceType, OriginDataType, IOriginDataItem, ActiveDataType, NewDataType, IItem } from "../interface/IDropdown";

/*
    option 밸리데이션
    @param {IDropdownConfig} option 옵션값
    @return {boolean} 밸리데이션 통과 여부 (true: 이상 없음, false: 이상 있음)
*/
export const validateOption = (option: IDropdownConfig): boolean => {
    if(!option?.parent) {
        console.log('Dropdown을 생성할 부모 node가 없습니다.');
        return false;
    }

    if(!Array.isArray(option?.data) || option?.data?.length === 0) {
        console.log('Dropdown을 생성할 데이터가 존재하지 않습니다.');
        return false;
    }

    return true;
}

/*
    (재귀) 최대 뎁스 찾기 - 각 뎁스별 뎁스 확인
    @param {DataType | IOriginDataItem} data 원형 데이터(_config.data)
    @param {number} depth 뎁스 (1부터 시작)
    @return {number | void} maxDepth 최대 뎁스
*/
export const checkDepth = (depth: number, data?: OriginDataType | IOriginDataItem): number | void => {
    let maxDepth = depth;

    if(Array.isArray(data) && data.length > 0) {
        data.forEach((v: IOriginDataItem) => {
            const calcDepth = checkDepth(depth + 1, v.children);
            if(typeof calcDepth === 'number' && maxDepth < calcDepth) {
                maxDepth = calcDepth;
            }
        });

        return maxDepth;
    } else {
        if(!Array.isArray(data) && !(data?.children)) {
            return maxDepth;
        }
    }
}

/*
    최대 뎁스 찾기
    @param {originData} data 원형 데이터(_config.data)
    @return {number} maxDepth 최대 뎁스
*/
export const findMaxDepth = (originData: OriginDataType) => {
    let maxDepth = 1;

    originData.forEach((v: IOriginDataItem) => {
        const depth = checkDepth(1, v.children);
        if(typeof depth === 'number' && maxDepth < depth) {
            maxDepth = depth;
        }
    });

    return maxDepth;
}

/*
    (재귀) 데이터 가공 - 각 뎁스별 데이터 가공
    @param {DataType} data 원형 데이터(_config.data)
    @param {number} curDepth 현재 뎁스 (curDepth는 1부터 시작, 가공 데이터의 뎁스 인덱스는 0부터 시작)
    @param {string} depthKey Map 저장에 사용할 키 (부모인덱스-본인인덱스, ex. 2depth이면 1-0)
    @param {NewDataType} newData 반환할 가공 데이터
    @return {NewDataType} newData
*/
export const setEachDepthNewData = (data: OriginDataType, curDepth: number, depthKey: string, newData: NewDataType): NewDataType => {
    let itemArr: IItem[] = [];

    data.forEach((v, i) => {
        const { title, value, children } = v;
        const key = curDepth === 1 ? `${i}` : `${depthKey}-${i}`;
        const data = newData[curDepth - 1];

        if(Array.isArray(data)) {
            data.push({ title, value: typeof value !== 'string' ? `${value}` : value, key });
        } else {
            itemArr.push({ title, value: typeof value !== 'string' ? `${value}` : value, key });
            data.set(depthKey, itemArr);
        }

        if(Array.isArray(children) && children.length > 0) {
            setEachDepthNewData(children, curDepth + 1, key, newData);
        }
    });

    return newData;
};

/*
    데이터 가공 (원형 데이터(트리) -> 사용하기 좋은 구조([1depth는 array, 2depth부터 각각 Map으로 구성])
    @param {OriginDataType} data 원형 데이터(_config.data)
    @param {number} maxDepth 원형 데이터의 최대 뎁스
    @return {NewDataType} newData
*/
export const setNewData = (originData: OriginDataType, maxDepth: number): NewDataType => {
    let newData = [] as NewDataType;
    let depth = 1;

    for(depth; depth <= maxDepth; depth++) {
        if(depth === 1) newData[depth - 1] = [];
        else newData[depth - 1] = new Map();
    }

    newData = setEachDepthNewData(originData, 1, '', newData);

    return newData;
};

/*
    가공한 데이터에서 현재 활성화된 요소 중 마지막 요소의 키값을 통해, 1뎁스부터 마지막 뎁스까지 활성화된 뎁스별 데이터 추출 
    @param {NewDataType} data 가공한 데이터
    @param {string} key 마지막 뎁스 요소의 키값 (why? 부모인덱스들이 전부 포함되어 있음)
    @return {ActiveDataType | null} activeData
*/
export const getActiveData = (newData: NewDataType, key: string): ActiveDataType => {
    if(!Array.isArray(newData) || newData.length === 0 || !key) return [];
    
    let activeData = [];
    const splitKey: number[] = key.split('-').map(v => parseInt(v, 10));
    const maxDepth = splitKey.length;

    for(let i = 0; i < maxDepth; i++) {
        const valueIndex = splitKey[i];
        const depthData = newData[i];

        if(Array.isArray(depthData)) {
            const depth1Arr =  newData[i];

            if(depthData[valueIndex]) {
                activeData[i] = depthData[valueIndex];
                continue;
            }
            return [];
        } else {
            const depthKey = splitKey.slice(0, i).join('-');
            const depthArr = depthData.get(depthKey) || [];

            if(Array.isArray(depthArr) && depthArr.length > 0 && !!depthArr[valueIndex]) {
                activeData[i] = depthArr[valueIndex];
                continue;
            }
            return [];
        }
    }

    return activeData;
};

/*
    activeValue 호출시 전달받은 valueArr을 통해 key값을 추출하는 함수
    @param {string[]} valueArr 뎁스별 value array
    @param {NewDataType} newData 가공한 메뉴 데이터
    @return {string | null} activeKey
*/
export const getActiveKeyFromValueArr = (valueArr: string[], newData: NewDataType): string | null => {
    let keyArr: string[] = [];
    let key: string | null = null;

    if(!Array.isArray(valueArr) || valueArr?.length === 0) return key;

    // 마지막 뎁스값 기준으로 키 찾기
    const curMaxDepth = valueArr.length;

    // 마지막 뎁스 요소 키값에는 부모뎁스도 알 수 있으므로, 마지막 뎁스에서 value값 동일한 요소의 키값 추출
    const lastIndex = curMaxDepth - 1;
    const value = valueArr[lastIndex];
    
    newData[lastIndex].forEach(v => {
        if(Array.isArray(v)) {
            v.forEach(x => {
                if(x?.value === value) {
                    keyArr.push(x?.key);
                }
            });
        } else {
            if(v?.value === value) {
                keyArr.push(v?.key);
            }
        }
    });

    if(keyArr.length > 0) {
        const copyKeyArr = [...keyArr];
        copyKeyArr.forEach((k, i) => {
            const splitKey = k.split('-')?.map(v => parseInt(v, 10));
            let parentKey = '';

            // 부모 뎁스 확인하여 최종 키값 추출
            for(let depth = 0; depth < splitKey.length; depth++) {
                const parentIndex = splitKey[depth];
                const data = newData[depth];
                const depthData = Array.isArray(data) ? data : data?.get(parentKey) || [];
                const parentData = Array.isArray(depthData) ? depthData[parentIndex] : null;
                const parentValue = parentData?.value;

                if(parentValue !== valueArr[depth]) {
                    keyArr.splice(i, 1);
                    break;
                }

                parentKey = splitKey.slice(0, depth + 1).join('-');
            }
        });
    }

    if(keyArr.length === 1) {
        key = keyArr[0];
    }

    return key;
};

/*
    현재 메뉴 키값이 마지막 뎁스인지 여부 확인
    @param {string} activeKey 활성화 키
    @param {NewDataType} newData 가공한 메뉴 데이터
    @param {number} maxDepth 최대 뎁스
    @return {boolean} 마지막 뎁스인지 여부
*/
export const getIsLastDepth = (activeKey: string, newData: NewDataType, maxDepth: number) => {
    if(!activeKey || !Array.isArray(newData) || !maxDepth) return false;

    const curMaxDepth = activeKey.split('-')?.length;
    const curNextDepthData = newData[curMaxDepth];
    const children = Array.isArray(curNextDepthData) ? curNextDepthData : curNextDepthData?.get(activeKey) || []; // curMaxDepth의 다음 뎁스 데이터

    return curMaxDepth === maxDepth || !Array.isArray(children) || children?.length === 0;
};

/*
    디폴트 활성화 key값 (뎁스별 첫번째 요소 활성화)
    @param {number} maxDepth 최대 뎁스
    @return {string}
*/
export const getDefaultActiveKey = (maxDepth: number = 0): string => {
    return Array(maxDepth).fill('0').join('-');
};

/*
    activeData를 {d1: value, d2: value, ...} 구조로 변경하는 함수
    @param {ActiveData} activeData 현재 활성화 데이터
    @return {{[key: string]: string}}
*/
export const setActiveDataToObject = (activeData: ActiveDataType): {[key: string]: string} => {
    let activeDataObj: {[key: string]: string} = {};

    if(Array.isArray(activeData) && activeData.length > 0) {
        activeData.forEach((v: IItem, i: number) => {
            activeDataObj[`d${i + 1}`] = v.value;
        });
    }

    return activeDataObj;
};

/*
    현재 디바이스가 pc인지 여부 확인
    @return {boolean}
*/
export const getIsPc = (): boolean => {
    return document.body.classList.contains('pc');
};

/*
    device 변경 (body className 변경)
    - Jest 단위테스트에 사용됨
    @param {DeviceType} device 변경하고자 하는 디바이스명
*/
export const switchDevice = (device: DeviceType) => {
    const bodyClassArr = document.body.className.split(' ');
    const deviceType = ['pc', 'tablet', 'mobile'];
    const curDeviceIndex = deviceType.findIndex(v => v === device);
    let newBodyClassArr = [];

    deviceType.splice(curDeviceIndex, 1);

    if(bodyClassArr.includes(device)) return;
    
    bodyClassArr.forEach(v => {
        if(!deviceType.includes(v)) {
            newBodyClassArr.push(v);
        }
    });
    
    newBodyClassArr.push(device);
    document.body.className = newBodyClassArr.join(' ');
};

/*
    state 변화 여부
    @param {any} originState
    @param {any} newState
    @return {boolean} isMutation state 변화 여부
*/
export const getIsMutation = (originState: any, newState: any): boolean => {
    return originState !== newState;
};